import React, { useState } from "react";
import {
  Edit3,
  Trash2,
  Users,
  BookOpen,
  Calendar,
  Clock,
  MoreVertical,
  UserCheck,
  GraduationCap,
} from "lucide-react";
import { Class } from "../../types/class";

interface ClassesTableProps {
  classes: Class[];
  loading: boolean;
  onEdit: (classItem: Class) => void;
  onDelete: (classId: string) => void;
  onBulkDelete: (classIds: string[]) => void;
  onManage?: (classId: string) => void;
}

const ClassesTable: React.FC<ClassesTableProps> = ({
  classes,
  loading,
  onEdit,
  onDelete,
  onBulkDelete,
  onManage,
}) => {
  const [selectedClasses, setSelectedClasses] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState<
    "name" | "grade" | "academicTerm" | "capacity"
  >("name");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");

  const handleSelectAll = (checked: boolean) => {
    setSelectedClasses(checked ? classes.map((c) => c._id) : []);
  };

  const handleSelectClass = (classId: string, checked: boolean) => {
    setSelectedClasses((prev) =>
      checked ? [...prev, classId] : prev.filter((id) => id !== classId)
    );
  };

  const handleSort = (
    field: "name" | "grade" | "academicTerm" | "capacity"
  ) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortOrder("asc");
    }
  };

  const sortedClasses = [...classes].sort((a, b) => {
    let aValue: string | number = "";
    let bValue: string | number = "";

    switch (sortBy) {
      case "name":
        aValue = a.name.toLowerCase();
        bValue = b.name.toLowerCase();
        break;
      case "grade":
        aValue = a.grade;
        bValue = b.grade;
        break;
      case "academicTerm":
        aValue = a.academicTerm || "";
        bValue = b.academicTerm || "";
        break;
      case "capacity":
        aValue = a.capacity || 0;
        bValue = b.capacity || 0;
        break;
    }

    if (sortOrder === "asc") {
      return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
    } else {
      return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
    }
  });

  const getStatusBadge = (isActive: boolean) => {
    return isActive ? "badge-success" : "badge-error";
  };

  const getGradeBadge = (grade: string) => {
    // You can customize this based on your grade system
    const badges = {
      "Grade 1": "badge-primary",
      "Grade 2": "badge-secondary",
      "Grade 3": "badge-accent",
      "Grade 4": "badge-info",
      "Grade 5": "badge-success",
      "Grade 6": "badge-warning",
      "Grade 7": "badge-error",
      "Grade 8": "badge-neutral",
    };
    return badges[grade as keyof typeof badges] || "badge-ghost";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="loading loading-spinner loading-lg"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Bulk Actions */}
      {selectedClasses.length > 0 && (
        <div className="flex items-center gap-4 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl shadow-md border-0">
          <span className="font-medium">
            {selectedClasses.length} class(es) selected
          </span>
          <button
            onClick={() => onBulkDelete(selectedClasses)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg shadow-md hover:shadow-lg hover:bg-red-700 transition-all duration-200 border-0"
          >
            <Trash2 className="h-4 w-4" />
            Delete Selected
          </button>
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto bg-white rounded-xl shadow-2xl hover:shadow-3xl transition-all duration-300 transform hover:-translate-y-1 border-0">
        <table className="table w-full">
          <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
            .
            <tr>
              <th>
                <label>
                  <input
                    type="checkbox"
                    className="checkbox"
                    checked={selectedClasses.length === classes.length}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                  />
                </label>
              </th>
              <th
                className="cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort("name")}
              >
                <div className="flex items-center gap-1">
                  <GraduationCap className="h-4 w-4" />
                  Class Name
                  {sortBy === "name" && (
                    <span className="text-xs">
                      {sortOrder === "asc" ? "↑" : "↓"}
                    </span>
                  )}
                </div>
              </th>
              <th
                className="cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort("grade")}
              >
                <div className="flex items-center gap-1">
                  <Calendar className="h-4 w-4" />
                  Grade
                  {sortBy === "grade" && (
                    <span className="text-xs">
                      {sortOrder === "asc" ? "↑" : "↓"}
                    </span>
                  )}
                </div>
              </th>
              <th
                className="cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort("academicTerm")}
              >
                <div className="flex items-center gap-1">
                  <Clock className="h-4 w-4" />
                  Academic Term
                  {sortBy === "academicTerm" && (
                    <span className="text-xs">
                      {sortOrder === "asc" ? "↑" : "↓"}
                    </span>
                  )}
                </div>
              </th>
              <th>
                <div className="flex items-center gap-1">
                  <BookOpen className="h-4 w-4" />
                  Subjects
                </div>
              </th>
              <th
                className="cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort("capacity")}
              >
                <div className="flex items-center gap-1">
                  <Users className="h-4 w-4" />
                  Students / Capacity
                  {sortBy === "capacity" && (
                    <span className="text-xs">
                      {sortOrder === "asc" ? "↑" : "↓"}
                    </span>
                  )}
                </div>
              </th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedClasses.map((classItem) => (
              <tr key={classItem._id} className="hover">
                <td>
                  <label>
                    <input
                      type="checkbox"
                      className="checkbox"
                      checked={selectedClasses.includes(classItem._id)}
                      onChange={(e) =>
                        handleSelectClass(classItem._id, e.target.checked)
                      }
                    />
                  </label>
                </td>
                <td>
                  <div>
                    <div className="font-semibold">{classItem.name}</div>
                    {classItem.section && (
                      <div className="text-sm text-gray-500">
                        Section: {classItem.section}
                      </div>
                    )}
                  </div>
                </td>
                <td>
                  <div className={`badge ${getGradeBadge(classItem.grade)}`}>
                    {classItem.grade}
                  </div>
                </td>
                <td>
                  <div className="flex items-center gap-1">
                    <Clock className="h-4 w-4 text-blue-500" />
                    {classItem.academicTerm || "Not Set"}
                  </div>
                </td>
                <td>
                  <div className="flex items-center gap-1">
                    <BookOpen className="h-4 w-4 text-green-500" />
                    {classItem.subjects?.length || 0}
                  </div>
                </td>
                <td>
                  <div className="flex items-center gap-1">
                    <UserCheck className="h-4 w-4 text-purple-500" />
                    {classItem.students?.length || 0}
                    {classItem.capacity && (
                      <span className="text-sm text-gray-500">
                        / {classItem.capacity}
                      </span>
                    )}
                  </div>
                </td>
                <td>
                  <div
                    className={`badge ${getStatusBadge(classItem.isActive)}`}
                  >
                    {classItem.isActive ? "Active" : "Inactive"}
                  </div>
                </td>
                <td>
                  <div className="dropdown dropdown-end">
                    <label tabIndex={0} className="btn btn-ghost btn-sm">
                      <MoreVertical className="h-4 w-4" />
                    </label>
                    <ul
                      tabIndex={0}
                      className="dropdown-content menu p-2 shadow-lg bg-base-100 rounded-xl w-52 border-0"
                    >
                      {onManage && (
                        <li>
                          <button
                            onClick={() => onManage(classItem._id)}
                            className="flex items-center gap-2 p-2 rounded-lg hover:bg-blue-50 hover:text-blue-600 transition-all duration-200"
                          >
                            <Users className="h-4 w-4" />
                            Manage Class
                          </button>
                        </li>
                      )}
                      <li>
                        <button
                          onClick={() => onEdit(classItem)}
                          className="flex items-center gap-2 p-2 rounded-lg hover:bg-indigo-50 hover:text-indigo-600 transition-all duration-200"
                        >
                          <Edit3 className="h-4 w-4" />
                          Edit Class
                        </button>
                      </li>
                      <li>
                        <button
                          onClick={() => onDelete(classItem._id)}
                          className="flex items-center gap-2 text-red-600 p-2 rounded-lg hover:bg-red-50 hover:text-red-700 transition-all duration-200"
                        >
                          <Trash2 className="h-4 w-4" />
                          Delete Class
                        </button>
                      </li>
                    </ul>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {classes.length === 0 && (
          <div className="text-center py-8">
            <GraduationCap className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">No classes found</p>
            <p className="text-sm text-gray-400">
              Create your first class to get started
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClassesTable;
